// src/components7/BottomNav.js
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FaUtensils, FaFire, FaHistory } from "react-icons/fa";

const TABS = [
  { path: "/", title: "Recipe Generator", icon: <FaUtensils size={17} />, animType: "anim-utensils" },
  { path: "/caloriecheck", title: "Calories & Nutrition", icon: <FaFire size={18} />, animType: "anim-fire" },
  { path: "/history", title: "History (Recipes & Calories)", icon: <FaHistory size={17} />, animType: "anim-history" },
];

const TAB_WIDTH = 58;
const TAB_GAP = 6;
const CONTAINER_PAD_LEFT = 6;

// Exact pixel X coordinate for the sliding white pill
const getPillOffset = (idx) => {
  if (idx < 0) return CONTAINER_PAD_LEFT;
  return CONTAINER_PAD_LEFT + idx * (TAB_WIDTH + TAB_GAP);
};

const BottomNav = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const navContainerRef = useRef(null);
  const navTimerRef = useRef(null);
  const animTimerRef = useRef(null);

  const getTabIndex = useCallback((pathname) => {
    if (pathname === "/" || pathname === "") return 0;
    if (pathname === "/caloriecheck") return 1;
    if (pathname === "/history" || pathname === "/calhistory") return 2;
    return -1;
  }, []);

  const routeIndex = getTabIndex(location.pathname);
  const [activeIdx, setActiveIdx] = useState(routeIndex >= 0 ? routeIndex : -1);
  const [lastValidIdx, setLastValidIdx] = useState(routeIndex >= 0 ? routeIndex : 0);
  const [animatingIdx, setAnimatingIdx] = useState(null);
  const [isNavHidden, setIsNavHidden] = useState(false);
  const lastScrollY = useRef(0);
  const isNavHiddenRef = useRef(false);

  // Auto-hide on swipe up / scroll down, auto-show on swipe down / scroll up
  useEffect(() => {
    let rafId = null;
    const SCROLL_THRESHOLD = 8;
    const TOP_THRESHOLD = 15;
    let touchStartY = 0;
    let touchStartX = 0;
    let touchCurrentY = 0;

    const getScrollTop = () => {
      return (
        window.pageYOffset ||
        window.scrollY ||
        document.documentElement.scrollTop ||
        document.body.scrollTop ||
        0
      );
    };

    const updateNavVisibility = (hidden) => {
      if (isNavHiddenRef.current !== hidden) {
        isNavHiddenRef.current = hidden;
        setIsNavHidden(hidden);
      }
    };

    const handleScroll = () => {
      if (rafId !== null) return;

      rafId = window.requestAnimationFrame(() => {
        const currentScrollY = getScrollTop();
        const delta = currentScrollY - lastScrollY.current;

        if (currentScrollY <= TOP_THRESHOLD) {
          // Always show at top of page
          updateNavVisibility(false);
        } else if (delta > SCROLL_THRESHOLD) {
          // Scrolling down (page moving up): hide bottom bar smoothly
          updateNavVisibility(true);
        } else if (delta < -SCROLL_THRESHOLD) {
          // Scrolling up (page moving down): reveal bottom bar smoothly
          updateNavVisibility(false);
        }

        lastScrollY.current = currentScrollY <= 0 ? 0 : currentScrollY;
        rafId = null;
      });
    };

    const handleTouchStart = (e) => {
      if (e.touches && e.touches[0]) {
        touchStartY = e.touches[0].clientY;
        touchStartX = e.touches[0].clientX;
        touchCurrentY = e.touches[0].clientY;
      }
    };

    const handleTouchMove = (e) => {
      if (!e.touches || !e.touches[0]) return;
      const currentY = e.touches[0].clientY;
      const currentX = e.touches[0].clientX;
      const deltaY = touchStartY - currentY; // positive = swipe finger up (scroll down)
      const deltaX = Math.abs(currentX - touchStartX);

      // Respond directly to vertical gestures
      if (Math.abs(deltaY) > deltaX) {
        const currentScrollY = getScrollTop();
        if (currentScrollY <= TOP_THRESHOLD && deltaY < 0) {
          updateNavVisibility(false);
        } else if (deltaY > 18) {
          // Intentional upward swipe (scroll down): hide bottom bar smoothly
          updateNavVisibility(true);
        } else if (deltaY < -18) {
          // Intentional downward swipe (scroll up): reveal bottom bar smoothly
          updateNavVisibility(false);
        }
      }
      touchCurrentY = currentY;
    };

    // Wheel event handler for desktop trackpad / mousewheel scrolling
    const handleWheel = (e) => {
      if (Math.abs(e.deltaY) > 3) {
        const currentScrollY = getScrollTop();
        if (currentScrollY <= TOP_THRESHOLD && e.deltaY < 0) {
          updateNavVisibility(false);
        } else if (e.deltaY > 0) {
          updateNavVisibility(true);
        } else if (e.deltaY < 0) {
          updateNavVisibility(false);
        }
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    document.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("wheel", handleWheel, { passive: true });
    window.addEventListener("touchstart", handleTouchStart, { passive: true });
    window.addEventListener("touchmove", handleTouchMove, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
      document.removeEventListener("scroll", handleScroll);
      window.removeEventListener("wheel", handleWheel);
      window.removeEventListener("touchstart", handleTouchStart);
      window.removeEventListener("touchmove", handleTouchMove);
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, []);

  // When changing routes/pages, ensure the bottom nav is visible
  useEffect(() => {
    isNavHiddenRef.current = false;
    setIsNavHidden(false);
    lastScrollY.current = 0;
  }, [location.pathname]);

  // Synchronize with external route changes (e.g. browser back/forward or in-page links)
  useEffect(() => {
    if (routeIndex >= 0) {
      setActiveIdx(routeIndex);
      setLastValidIdx(routeIndex);
    } else {
      setActiveIdx(-1);
    }
  }, [routeIndex]);

  // Clean up any pending navigation and animation timers on unmount
  useEffect(() => {
    return () => {
      if (navTimerRef.current) clearTimeout(navTimerRef.current);
      if (animTimerRef.current) clearTimeout(animTimerRef.current);
    };
  }, []);

  const triggerIconAnimation = (targetIdx) => {
    setAnimatingIdx(null);
    if (animTimerRef.current) clearTimeout(animTimerRef.current);
    
    // Trigger animation in next frame for clean CSS restart
    requestAnimationFrame(() => {
      setAnimatingIdx(targetIdx);
      animTimerRef.current = setTimeout(() => {
        setAnimatingIdx(null);
      }, 900);
    });
  };

  const handleTabClick = (e, targetIdx, targetPath) => {
    e.preventDefault();

    // Trigger icon animation on tap (even if already on active tab)
    triggerIconAnimation(targetIdx);

    if (activeIdx === targetIdx && location.pathname === targetPath) return;

    if (navTimerRef.current) {
      clearTimeout(navTimerRef.current);
    }

    // Direction based on menu page index:
    // Moving right in menu (e.g. 0 -> 1 or 1 -> 2): slide page from right to left
    // Moving left in menu (e.g. 2 -> 1 or 1 -> 0): slide page from left to right
    const direction = targetIdx > activeIdx ? "slide-from-right" : "slide-from-left";

    // 1. Immediately slide pill & update active highlight state
    setActiveIdx(targetIdx);
    setLastValidIdx(targetIdx);

    // 2. Coordinated physical slide timing between pill and page transition
    navTimerRef.current = setTimeout(() => {
      navigate(targetPath, { state: { slideDirection: direction } });
    }, 140);
  };

  const isPillVisible = routeIndex >= 0 && activeIdx >= 0;
  const currentPillIdx = activeIdx >= 0 ? activeIdx : (lastValidIdx >= 0 ? lastValidIdx : 0);
  const pillLeft = getPillOffset(currentPillIdx);

  return (
    <nav
      className={`floating-bottom-nav ${isNavHidden ? "nav-hidden" : ""}`}
      aria-label="Bottom Navigation"
    >
      <div className="nav-items-container" ref={navContainerRef}>
        {/* Hardware-Accelerated Ultra-Smooth Sliding White Pill */}
        <div
          className="nav-slider-pill"
          style={{
            transform: `translate3d(${pillLeft}px, 0, 0) scale(${isPillVisible ? 1 : 0.8})`,
            opacity: isPillVisible ? 1 : 0,
            pointerEvents: "none",
          }}
          aria-hidden="true"
        />

        {TABS.map((tab, idx) => {
          const isActive = routeIndex >= 0 && activeIdx === idx;
          const isAnimating = animatingIdx === idx;
          return (
            <a
              key={tab.path}
              href={tab.path}
              onClick={(e) => handleTabClick(e, idx, tab.path)}
              className={`nav-capsule-item ${isActive ? "is-active" : ""} ${isAnimating ? "is-clicked" : ""}`}
              title={tab.title}
              aria-label={tab.title}
            >
              <span className={`nav-icon-wrap ${isAnimating ? tab.animType : ""}`}>
                {tab.icon}
              </span>
            </a>
          );
        })}
      </div>
    </nav>
  );
};

export default BottomNav;

